/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';

import { config } from './config';

/** Shared axios client; token provider injected at runtime (no auth dependency). */
export const apiClient: AxiosInstance = axios.create({
  baseURL: config.api.baseUrl,
  timeout: 30_000,
  headers: { Accept: 'application/json' },
});

type TokenProvider = () => Promise<string | null | undefined> | string | null | undefined;

let getToken: TokenProvider = () => null;

/** Register the async access-token provider (MSAL acquires tokens silently). */
export function setAuthTokenProvider(provider: TokenProvider): void {
  getToken = provider;
}

type UnauthorizedHandler = (error: unknown) => void;

let onUnauthorized: UnauthorizedHandler | null = null;

/**
 * Register a handler invoked when the backend rejects a request with 401. The app uses this to
 * route the user to the Unauthorized page instead of silently degrading (e.g. showing empty or
 * partial data) when the session is missing/expired or lacks access.
 */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  onUnauthorized = handler;
}

apiClient.interceptors.request.use(async (request: InternalAxiosRequestConfig) => {
  const token = await getToken();
  if (token) {
    request.headers.set('Authorization', `Bearer ${token}`);
  }
  return request;
});

/**
 * A data call answered with a web page: the SPA's (or a gateway's) HTML fallback for a path the
 * backend does not serve. It arrives as a 200, so without this the page's markup would reach the
 * views as their "data" - a string where rows were expected - and crash them.
 */
function isHtmlFallback(response: AxiosResponse): boolean {
  const responseType = response.config?.responseType;
  const expectsData = !responseType || responseType === 'json';
  return expectsData && String(response.headers?.['content-type'] ?? '').includes('text/html');
}

apiClient.interceptors.response.use(
  (response) => {
    if (!isHtmlFallback(response)) return response;
    if (import.meta.env.DEV) {
      console.error('[api] request failed', response.config?.url, 'HTML instead of JSON');
    }
    return Promise.reject(
      new AxiosError(
        'Expected JSON but the server answered with an HTML page',
        AxiosError.ERR_BAD_RESPONSE,
        response.config,
        response.request,
        response,
      ),
    );
  },
  (error) => {
    const status = error?.response?.status;
    if (status === 401 && onUnauthorized) {
      onUnauthorized(error);
    }
    if (import.meta.env.DEV) {
      console.error('[api] request failed', error?.config?.url, status);
    }
    return Promise.reject(error);
  },
);
